<template>
  <v-hover>
    <div class="mx-2 mb-3" slot-scope="{ hover }" :class="[hover ? 'elevation-5' : '']">
      <v-card dark :class="{ 'grey--text text--lighten-2': !hover }">
        <v-card-title class="py-0 px-0 font-weight-regular">
          <v-tooltip bottom :open-delay="300">
            <template v-slot:activator="{ on }">
              <v-chip :color="eventType.color" label small class="unselectable mr-0">
                <v-icon v-on="on">{{ eventType.icon }}</v-icon>
              </v-chip>
            </template>
            <span v-html="eventType.text"></span>
          </v-tooltip>

          <v-chip :color="eventType.eventColor" label small class="unselectable ml-0">
            <span>{{ eventType.title }}</span>
            <v-icon class="ml-1">{{ eventType.eventIcon }}</v-icon>
          </v-chip>

          <v-chip label small class="d-flex align-center unselectable">
            <v-icon class="mr-1">event</v-icon>
            {{ format(item.resultDate) }} {{ timezoneId }}
          </v-chip>

          <v-tooltip bottom :open-delay="300">
            <template v-slot:activator="{ on }">
              <v-chip v-on="on" label small class="mx-1 d-flex align-center unselectable">
                <v-icon class="mr-1">folder_special</v-icon>
                {{ prettyPrintServiceName(item.serviceName) }}
              </v-chip>
            </template>
            <span> <strong>{{ prettyPrintServiceName(item.serviceName) }}</strong> file type. </span>
          </v-tooltip>

          <ExecutionFileVersionChip @click:version="goToFilterVersion($event)" small :fileVersions="item.relatedFileVersions" :isLatestVersion="item.isLatestVersion" />

        </v-card-title>
      </v-card>
      <v-card class="card-bg-2">
        <v-card-text class="elevation-0 py-0 px-0 card-bg-1">
          <TestRun v-if="item.isTestRun" is-root :hover="hover" :item="item" />
          <RollbackRun v-else-if="item.isRollback" is-root :hover="hover" :item="item" />
          <DefaultRun v-else :hover="hover" is-root :item="item" />
        </v-card-text>
        <template v-if="(item.isSuccess && item.importId && !(item.isTestRun || item.isRollback || item.isRolledBack)) || (item.executionId && (item.isIngestion || item.isTestRun))">
          <v-divider />
          <v-card-actions>
            <template v-if="item.isSuccess && item.importId && !(item.isTestRun || item.isRollback || item.isRolledBack)">
              <SecondaryBtn icon="storage" small :to="{ name: 'import', params: { importId: item.importId }}" v-if="item.serviceLink != 'ecosurvey_edd_ingestion'">
                View import details
              </SecondaryBtn>
              <v-divider v-if="item.executionId != null && item.isIngestion && item.serviceLink != 'ecosurvey_edd_ingestion'" class="mx-2" vertical />
              <RollbackImportAction v-if="isSystemAdmin || isTenantAdmin" :importId="item.importId" />              
            </template>

            <template v-if="item.executionId">
              <v-divider v-if="item.isIngestion || item.isTestRun" class="mx-2" vertical />
              <SecondaryBtn v-if="item.isIngestion || item.isTestRun" small icon="pending_actions" @click="reviewFile(item)">
                Review file
              </SecondaryBtn>

              <!-- Hide for now because the logs need to be fixed
              <SecondaryBtn  icon="text_snippet" small @click="showUserLogs(item)">
                View logs
              </SecondaryBtn>
              -->
            </template>
          </v-card-actions>
        </template>
      </v-card>
    </div>
  </v-hover>
</template>

<script>
  import moment from 'moment'
  import { pluralize } from '@/helpers/strings'
  import { getMediaTypeIcon } from '@/helpers/mediaTypes'
  import { prettyDateTimeFormat } from '@/components/mixins/MomentUtil'
  import { prettyPrintServiceName } from '@/helpers/datasets/ingestions'

  import TestRun from './ImportTypes/TestRun'
  import DefaultRun from './ImportTypes/DefaultRun'
  import RollbackRun from './ImportTypes/RollbackRun'

  import { createNamespacedHelpers } from 'vuex'
  const ingestions = createNamespacedHelpers('ingestions')
  const authenticate = createNamespacedHelpers('authenticate')

  export default {
    components: {
      TestRun,
      DefaultRun,
      RollbackRun,
    },
    props: {
      indexId: String,
      dark: Boolean,
    },
    computed: {
      ...ingestions.mapState(['importsIndex']),
      ...authenticate.mapGetters(['isSystemAdmin', 'isTenantAdmin']),
      item() {
        if (!this.indexId) return {}
        else {
          let item = this.importsIndex[this.indexId]
          // item.isRollback = false
          // item.isTestRun = true
          return item
        }
      },
      eventTypeClass() {
        let cclass = 'py-0 px-2 mx-0 d-flex align-center '
        // else if (this.item.isSuccess) return '#606c64'
        // else return '#6c6060'
        if (this.item.isIngestion) return cclass + ' info--text'
        if (this.item.isRollback) return cclass + 'success--text'
        if (this.item.isTestRun) return cclass + 'warning--text'
        return cclass + 'secondary'
      },
      eventType() {
        let isSuccess = this.item.isSuccess
        // let text = isSuccess ? 'The import was successful' : 'The import failed'
        let icon = isSuccess ? 'done' : 'error'
        let color = isSuccess ? 'success' : 'error'
        if (this.item.isIngestion) {
          return {
            title: 'Ingestion',
            text: 'Import was a data ingestion.',
            eventColor: 'info darken-',
            eventIcon: 'cloud_upload',
            color,
            icon,
          }
        } else if (this.item.isTestRun) {
          return {
            title: 'Test run',
            text: 'Import was a test run. No new data was imported.',

            eventColor: 'warning darken-',
            eventIcon: 'construction',
            icon,
            color,
          }
        } else if (this.item.isRollback) {
          return {
            title: 'Rollback',
            text: 'Import was rolled back.',
            eventColor: 'error darken-',
            eventIcon: 'settings_backup_restore',
            icon,
            color,
          }
        }
      },
      timezoneId() {
        var tz = Intl.DateTimeFormat().resolvedOptions().timeZone
        return tz
      },
    },
    methods: {
      pluralize,
      getMediaTypeIcon,
      prettyPrintServiceName,

      showUserLogs(item) {
        this.$emit('show:logs', item)
      },
      format(date) {
        var momentObject = moment.utc(date).toDate()        
        return moment(momentObject).format(prettyDateTimeFormat)
      },      
      goToFilterVersion(version) {
        this.$router.push({ name: 'allimports', query: { search: version.id } })
      },
      reviewFile(item) {
        if (item.importId && item.serviceName == 'Hatfield.Services.Importer.A125Data') {
          this.$router.push({ name: 'dataset', params: { id: item.fileId  }})
          
        } else if (item.isIngestion || item.isTestRun) {
          this.$router.push({ name: 'reviewingestionresults', params: { executionId: item.executionId }})
        }
      },
    },
  }
</script>